export type ResponseType =
  | 'Substantive'
  | 'Objection Only'
  | 'References Documents'

export type WorkflowStatus = 'Current' | 'Amended' | 'Not Yet Received'

export type DiscoveryType =
  | 'Form Interrogatories'
  | 'Special Interrogatories'
  | 'Requests for Admission'
  | 'Document Requests'

export interface DiscoveryResponse {
  id: string
  party: string
  set: string
  type: DiscoveryType
  number: string
  question: string
  response: string
  responseTypes: ResponseType[]
  status: WorkflowStatus
}

export const PARTIES = [
  'Defendant 1 — Acme Corp',
  'Defendant 2 — Smith Holdings',
  'Defendant 3 — Pacific Industries',
  'Defendant 4 — Global Logistics',
]

export const SETS = ['Set 1', 'Set 2', 'Set 3', 'Set 4', 'Set 5']

export const TYPES: DiscoveryType[] = [
  'Form Interrogatories',
  'Special Interrogatories',
  'Requests for Admission',
  'Document Requests',
]

export const RESPONSE_TYPES: ResponseType[] = [
  'Substantive',
  'Objection Only',
  'References Documents',
]

export const WORKFLOW_STATUSES: WorkflowStatus[] = [
  'Current',
  'Amended',
  'Not Yet Received',
]

export const mockData: DiscoveryResponse[] = [
  {
    id: '1',
    party: 'Defendant 1 — Acme Corp',
    set: 'Set 1',
    type: 'Special Interrogatories',
    number: 'Special No. 1',
    question:
      'State all facts upon which you base your contention that the product was manufactured in accordance with industry standards.',
    response:
      'Responding Party objects to this interrogatory on the grounds that it is overly broad, unduly burdensome, and seeks information protected by the attorney-client privilege. Subject to and without waiving these objections, Responding Party states that the product underwent rigorous testing pursuant to ISO 9001 standards.',
    responseTypes: ['Objection Only', 'Substantive'],
    status: 'Current',
  },
  {
    id: '2',
    party: 'Defendant 1 — Acme Corp',
    set: 'Set 1',
    type: 'Form Interrogatories',
    number: 'Form 8.1',
    question:
      'Identify all persons who assisted in the preparation of these responses.',
    response:
      'Jane Doe, General Counsel for Acme Corp; John Smith, Director of Engineering.',
    responseTypes: ['Substantive'],
    status: 'Current',
  },
  {
    id: '3',
    party: 'Defendant 1 — Acme Corp',
    set: 'Set 2',
    type: 'Special Interrogatories',
    number: 'Special No. 14',
    question:
      'Identify all documents relating to the design, testing, and modification of the braking mechanism.',
    response: 'See documents produced at ACME_001452 through ACME_001983.',
    responseTypes: ['References Documents'],
    status: 'Current',
  },
  {
    id: '4',
    party: 'Defendant 2 — Smith Holdings',
    set: 'Set 1',
    type: 'Special Interrogatories',
    number: 'Special No. 4',
    question:
      'Describe in detail the reporting structure of the quality assurance department from 2018 to 2021.',
    response:
      "Objection. Vague and ambiguous as to 'reporting structure'. Furthermore, this request is not reasonably calculated to lead to the discovery of admissible evidence.",
    responseTypes: ['Objection Only'],
    status: 'Current',
  },
  {
    id: '5',
    party: 'Defendant 2 — Smith Holdings',
    set: 'Set 1',
    type: 'Form Interrogatories',
    number: 'Form 15.1',
    question:
      'Identify each denial of a material allegation and each special or affirmative defense in your pleadings.',
    response:
      'Responding Party incorporates by reference its First Amended Answer filed on March 12, 2023.',
    responseTypes: ['References Documents'],
    status: 'Amended',
  },
  {
    id: '6',
    party: 'Defendant 3 — Pacific Industries',
    set: 'Set 1',
    type: 'Special Interrogatories',
    number: 'Special No. 7',
    question:
      'State the date on which you first became aware of the alleged defect in the Model X-100.',
    response:
      'Responding Party first received a customer complaint regarding the Model X-100 on or about November 14, 2019.',
    responseTypes: ['Substantive'],
    status: 'Current',
  },
  {
    id: '7',
    party: 'Defendant 3 — Pacific Industries',
    set: 'Set 2',
    type: 'Special Interrogatories',
    number: 'Special No. 22',
    question:
      'Identify all communications between your engineering team and Acme Corp regarding the safety specifications.',
    response:
      'Objection. Overly broad and seeks confidential business information. Subject to the protective order, see PAC_004511 to PAC_005022.',
    responseTypes: ['Objection Only', 'References Documents'],
    status: 'Current',
  },
  {
    id: '8',
    party: 'Defendant 4 — Global Logistics',
    set: 'Set 1',
    type: 'Form Interrogatories',
    number: 'Form 4.1',
    question:
      'At the time of the INCIDENT, was there in effect any policy of insurance through which you were or might be insured?',
    response:
      'Yes. Commercial General Liability Policy No. GL-99821 issued by Travelers Insurance.',
    responseTypes: ['Substantive'],
    status: 'Current',
  },
  {
    id: '9',
    party: 'Defendant 4 — Global Logistics',
    set: 'Set 2',
    type: 'Special Interrogatories',
    number: 'Special No. 11',
    question:
      'Describe the chain of custody for the shipment of the Model X-100 units from the manufacturing facility to the distribution center.',
    response:
      'The units were loaded onto Fleet Vehicle 44 on October 2, 2020, and arrived at the distribution center on October 5, 2020. See shipping manifests GL_00210-GL_00215.',
    responseTypes: ['Substantive', 'References Documents'],
    status: 'Current',
  },
  {
    id: '10',
    party: 'Defendant 1 — Acme Corp',
    set: 'Set 3',
    type: 'Special Interrogatories',
    number: 'Special No. 45',
    question:
      'Explain the rationale for changing the material of the casing from aluminum to polymer in the 2021 redesign.',
    response:
      'Objection. Assumes facts not in evidence and mischaracterizes the nature of the redesign. The polymer was selected for its superior thermal resistance properties.',
    responseTypes: ['Objection Only', 'Substantive'],
    status: 'Amended',
  },
  {
    id: '11',
    party: 'Defendant 2 — Smith Holdings',
    set: 'Set 2',
    type: 'Form Interrogatories',
    number: 'Form 17.1',
    question:
      'Is your response to each request for admission served with these interrogatories an unqualified admission?',
    response: 'No. See responses to Requests for Admission, Set 2.',
    responseTypes: ['References Documents'],
    status: 'Current',
  },
  {
    id: '12',
    party: 'Defendant 3 — Pacific Industries',
    set: 'Set 3',
    type: 'Special Interrogatories',
    number: 'Special No. 33',
    question:
      'Identify all prior lawsuits in which you have been named as a defendant alleging product liability for similar devices.',
    response:
      'Objection. Irrelevant and overly burdensome. Responding Party declines to answer.',
    responseTypes: ['Objection Only'],
    status: 'Current',
  },
  {
    id: '13',
    party: 'Defendant 4 — Global Logistics',
    set: 'Set 3',
    type: 'Special Interrogatories',
    number: 'Special No. 19',
    question:
      'Did any of your employees report damage to the packaging of the Model X-100 units upon arrival?',
    response: '',
    responseTypes: [],
    status: 'Not Yet Received',
  },
  {
    id: '14',
    party: 'Defendant 1 — Acme Corp',
    set: 'Set 2',
    type: 'Form Interrogatories',
    number: 'Form 12.1',
    question:
      'State the name, ADDRESS, and telephone number of each individual who witnessed the INCIDENT.',
    response:
      'Responding Party was not present at the incident and has no personal knowledge of any witnesses other than those identified in the police report.',
    responseTypes: ['Substantive'],
    status: 'Current',
  },
  {
    id: '15',
    party: 'Defendant 2 — Smith Holdings',
    set: 'Set 3',
    type: 'Special Interrogatories',
    number: 'Special No. 28',
    question:
      'Detail the financial relationship between Smith Holdings and Acme Corp during the fiscal year 2020.',
    response:
      'Objection. Seeks proprietary financial data. Subject to the objection, Smith Holdings held a 15% equity stake in Acme Corp during the specified period.',
    responseTypes: ['Objection Only', 'Substantive'],
    status: 'Current',
  },
  {
    id: '16',
    party: 'Defendant 3 — Pacific Industries',
    set: 'Set 1',
    type: 'Form Interrogatories',
    number: 'Form 2.6',
    question:
      'State your present residence ADDRESS, and all other residence ADDRESSES for the past five years.',
    response: 'Objection. Interrogatory is inapplicable to a corporate entity.',
    responseTypes: ['Objection Only'],
    status: 'Current',
  },
  {
    id: '17',
    party: 'Defendant 4 — Global Logistics',
    set: 'Set 1',
    type: 'Special Interrogatories',
    number: 'Special No. 2',
    question: 'Identify the driver of Fleet Vehicle 44 on October 2, 2020.',
    response:
      'Michael Chang, employed by Global Logistics from 2018 to present.',
    responseTypes: ['Substantive'],
    status: 'Current',
  },
  {
    id: '18',
    party: 'Defendant 1 — Acme Corp',
    set: 'Set 3',
    type: 'Special Interrogatories',
    number: 'Special No. 50',
    question:
      'Admit that the internal testing conducted in August 2019 revealed a failure rate of 4.2%.',
    response:
      'Objection. This is improperly framed as a request for admission. Notwithstanding the objection, the August 2019 report speaks for itself. See ACME_008991.',
    responseTypes: ['Objection Only', 'References Documents'],
    status: 'Current',
  },
  {
    id: '19',
    party: 'Defendant 2 — Smith Holdings',
    set: 'Set 1',
    type: 'Special Interrogatories',
    number: 'Special No. 9',
    question:
      'Who authorized the release of the marketing materials attached as Exhibit A to the Complaint?',
    response:
      'The marketing materials were approved by the joint marketing committee, comprising representatives from both Smith Holdings and Acme Corp.',
    responseTypes: ['Substantive'],
    status: 'Amended',
  },
  {
    id: '20',
    party: 'Defendant 3 — Pacific Industries',
    set: 'Set 2',
    type: 'Form Interrogatories',
    number: 'Form 16.1',
    question:
      'Do you contend that any person, other than you or plaintiff, contributed to the occurrence of the INCIDENT?',
    response:
      "Yes. Responding Party contends that Plaintiff's misuse of the product was the sole proximate cause of the incident.",
    responseTypes: ['Substantive'],
    status: 'Current',
  },
  {
    id: '21',
    party: 'Defendant 4 — Global Logistics',
    set: 'Set 2',
    type: 'Form Interrogatories',
    number: 'Form 8.2',
    question:
      'If you claim that any person is responsible for the INCIDENT, identify each such person.',
    response: '',
    responseTypes: [],
    status: 'Not Yet Received',
  },
  {
    id: '22',
    party: 'Defendant 1 — Acme Corp',
    set: 'Set 1',
    type: 'Special Interrogatories',
    number: 'Special No. 12',
    question:
      'Describe the process by which quality control audits were conducted on the assembly line.',
    response:
      'Audits were conducted randomly on 5% of all units produced daily by the shift supervisor, following the checklist in the QC Manual (ACME_000112).',
    responseTypes: ['Substantive', 'References Documents'],
    status: 'Current',
  },
  {
    id: '23',
    party: 'Defendant 2 — Smith Holdings',
    set: 'Set 3',
    type: 'Special Interrogatories',
    number: 'Special No. 35',
    question:
      'Identify all board meetings where the safety of the Model X-100 was discussed.',
    response:
      'Objection. Protected by attorney-client privilege and work product doctrine as counsel was present to provide legal advice regarding potential liability.',
    responseTypes: ['Objection Only'],
    status: 'Current',
  },
  {
    id: '24',
    party: 'Defendant 3 — Pacific Industries',
    set: 'Set 3',
    type: 'Special Interrogatories',
    number: 'Special No. 40',
    question:
      'State the total number of Model X-100 units manufactured between January 1, 2020, and December 31, 2020.',
    response: 'Approximately 14,500 units.',
    responseTypes: ['Substantive'],
    status: 'Amended',
  },
  // New RFA and Doc Request rows
  {
    id: '25',
    party: 'Defendant 1 — Acme Corp',
    set: 'Set 1',
    type: 'Requests for Admission',
    number: 'RFA No. 1',
    question:
      'Admit that Acme Corp manufactured the Model X-100 pressure valve assembly involved in the INCIDENT.',
    response: 'Admitted.',
    responseTypes: ['Substantive'],
    status: 'Current',
  },
  {
    id: '26',
    party: 'Defendant 2 — Smith Holdings',
    set: 'Set 2',
    type: 'Requests for Admission',
    number: 'RFA No. 5',
    question:
      'Admit that you received notice of the recall issued by Acme Corp on or before December 1, 2020.',
    response:
      'Objection. Vague and ambiguous as to "notice". Subject to and without waiving the objection, Denied.',
    responseTypes: ['Objection Only', 'Substantive'],
    status: 'Current',
  },
  {
    id: '27',
    party: 'Defendant 3 — Pacific Industries',
    set: 'Set 1',
    type: 'Document Requests',
    number: 'Doc Req No. 3',
    question:
      'All documents relating to quality control testing of the Model X-100 units prior to distribution.',
    response:
      'Responding Party will produce non-privileged documents responsive to this request in its possession, custody, or control. See documents produced at PAC-001001 through PAC-001450.',
    responseTypes: ['References Documents'],
    status: 'Current',
  },
  {
    id: '28',
    party: 'Defendant 4 — Global Logistics',
    set: 'Set 1',
    type: 'Document Requests',
    number: 'Doc Req No. 7',
    question:
      'All shipping manifests and bills of lading for the transport of Model X-100 units from Acme Corp to Smith Holdings.',
    response:
      'Objection. Overly broad and unduly burdensome. Responding Party will produce documents limited to the timeframe of October 1, 2020 to December 31, 2020.',
    responseTypes: ['Objection Only', 'References Documents'],
    status: 'Amended',
  },
  {
    id: '29',
    party: 'Defendant 1 — Acme Corp',
    set: 'Set 2',
    type: 'Requests for Admission',
    number: 'RFA No. 12',
    question:
      'Admit that the polymer casing used in the 2021 redesign was less expensive than the aluminum casing used previously.',
    response: '',
    responseTypes: [],
    status: 'Not Yet Received',
  },
  {
    id: '30',
    party: 'Defendant 2 — Smith Holdings',
    set: 'Set 3',
    type: 'Document Requests',
    number: 'Doc Req No. 14',
    question:
      'All internal emails discussing the safety or reliability of the Model X-100.',
    response:
      'Objection. Seeks information protected by the attorney-client privilege and work product doctrine. A privilege log will be provided.',
    responseTypes: ['Objection Only'],
    status: 'Current',
  },
]
